import { gql } from '@apollo/client';
import axios from 'axios';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';

const CRIAR_USUARIO_E_ATLETA = gql`
  mutation CriarUsuarioEAtleta($name: String!, $email: String!, $password: String!, $cpf: String!) {
    criarUsuarioEAtleta(
      atleta: { cpf: $cpf, dataNascimento: "", cidadeId: null },
      usuario: { nome: $name, email: $email, senha: $password, role: "ATLETA", ativo: true }
    ) {
      id
      usuario {
        id
        nome
        email
      }
    }
  }
`;

export const criarUsuarioEAtleta = async (
  name: string,
  email: string,
  password: string,
  cpf: string
) => {
  const { data } = await apolloClient.mutate({
    mutation: CRIAR_USUARIO_E_ATLETA,
    variables: { name, email, password, cpf },
  });
  return data?.criarUsuarioEAtleta;
};

const CRIAR_USUARIO_E_ATLETA_OPCIONAL = gql`
  mutation CriarUsuarioEAtletaOpcional(
    $name: String!
    $email: String!
    $password: String
    $cpf: String
    $telefone: String
    $dataNascimento: String
    $cidadeId: ID
  ) {
    criarUsuarioEAtleta(
      atleta: {
        cpf: $cpf
        dataNascimento: $dataNascimento
        cidadeId: $cidadeId
        telefone: $telefone
      }
      usuario: {
        nome: $name
        email: $email
        senha: $password
        role: "ATLETA"
        ativo: true
      }
    ) {
      id
      usuario {
        id
        nome
        email
        username
        imagemUrl
      }
    }
  }
`;

export interface CriarUsuarioEAtletaOpcionalInput {
  name: string;
  email: string;
  password?: string;
  cpf?: string;
  telefone?: string;
  dataNascimento?: string;
  cidadeId?: string;
}

export interface CriarUsuarioEAtletaOpcionalResponse {
  id: string;
  usuario: {
    id: string;
    nome: string;
    email: string;
    username?: string | null;
    imagemUrl?: string | null;
  };
}

export interface CriarUsuarioEAtletaOpcionalData {
  criarUsuarioEAtleta: CriarUsuarioEAtletaOpcionalResponse;
}

export const criarUsuarioEAtletaOpcional = async (
  input: CriarUsuarioEAtletaOpcionalInput
): Promise<CriarUsuarioEAtletaOpcionalResponse | undefined> => {
  const { data } = await apolloClient.mutate<CriarUsuarioEAtletaOpcionalData>({
    mutation: CRIAR_USUARIO_E_ATLETA_OPCIONAL,
    variables: {
      name: input.name,
      email: input.email,
      password: input.password || null,
      cpf: input.cpf || null,
      telefone: input.telefone || null,
      dataNascimento: input.dataNascimento || null,
      cidadeId: input.cidadeId || null,
    },
  });
  return data?.criarUsuarioEAtleta;
};

export const ATUALIZAR_USUARIO = gql`
  mutation AtualizarUsuario($id: ID!, $nome: String!, $email: String!, $username: String!) {
    atualizarUsuario(id: $id, input: { nome: $nome, email: $email, username: $username }) {
      id
    }
  }
`;

export interface AtualizarUsuarioVars {
  id: string;
  nome: string;
  email: string;
  username: string;
}

const restUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT + 'api/';

export const atualizarImagemUsuario = async (id: string, arquivo: File) => {
  const formData = new FormData();
  formData.append('arquivo', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<{ imagemUrl: string }>(
      `${restUrl}usuarios/${id}/imagem`,
      formData,
      { headers }
    );
    return response.data.imagemUrl;
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }

    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};

export const atualizarBannerUrl = async (id: string, arquivo: File) => {
  const formData = new FormData();
  formData.append('arquivo', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<{ bannerUrl: string }>(
      `${restUrl}usuarios/${id}/banner`,
      formData,
      { headers }
    );
    return response.data.bannerUrl;
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }

    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};
